import React, { useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { Download } from 'lucide-react';
import {
  RECOVERY_KEY_FILENAME,
  RECOVERY_KEY_MISMATCH_MESSAGE,
  downloadRecoveryKeyFile,
  formatRecoveryKey,
  normalizeRecoveryKey,
  recoveryKeyFileBody,
} from '../../../services/chat/chatKeys';

export interface RecoveryKeyStepProps {
  recoveryKey: string;
  /** The account e-mail (uid when unavailable) written into line 1 of the file. */
  email: string;
  onConfirmed: () => void;
  confirmLabel?: string;
  disabled?: boolean;
}

/**
 * Shows a freshly generated recovery key once (8 groups of 4, no copy button),
 * offers the download, and requires one full re-entry before the caller may
 * continue. Shared by setup, identity reset, and "Make a new recovery key".
 */
export function RecoveryKeyStep({
  recoveryKey,
  email,
  onConfirmed,
  confirmLabel = 'Continue',
  disabled = false,
}: RecoveryKeyStepProps) {
  const [entry, setEntry] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const handleConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    if (disabled || !entry) return;
    if (normalizeRecoveryKey(entry) === recoveryKey) {
      setMismatch(false);
      onConfirmed();
    } else {
      setMismatch(true);
    }
  };

  return (
    <Box component="form" noValidate onSubmit={handleConfirm} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="warning">
        This recovery key is shown once. Download the file and keep it somewhere safe — it is the only
        way to unlock chat if you lose your passkeys.
      </Alert>
      <Box
        aria-label="Your recovery key"
        sx={{
          fontFamily: 'monospace',
          fontSize: 18,
          letterSpacing: 1,
          textAlign: 'center',
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          userSelect: 'all',
        }}
      >
        {formatRecoveryKey(recoveryKey)}
      </Box>
      <Button
        variant="outlined"
        startIcon={<Download size={16} aria-hidden />}
        onClick={() => downloadRecoveryKeyFile(recoveryKeyFileBody(recoveryKey, email))}
        disabled={disabled}
        sx={{ textTransform: 'none' }}
      >
        Download {RECOVERY_KEY_FILENAME}
      </Button>
      <Typography variant="body2" color="text.secondary">
        Type the full recovery key to confirm you have saved it.
      </Typography>
      {mismatch && <Alert severity="error">{RECOVERY_KEY_MISMATCH_MESSAGE}</Alert>}
      <TextField
        label="Recovery key"
        value={entry}
        onChange={(event) => setEntry(event.target.value)}
        size="small"
        fullWidth
        disabled={disabled}
        autoComplete="off"
        inputProps={{ spellCheck: false, 'aria-label': 'Type your recovery key' }}
      />
      <Button type="submit" variant="contained" disabled={disabled || !entry} sx={{ minHeight: 40 }}>
        {confirmLabel}
      </Button>
    </Box>
  );
}
